import { useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Modal, Row, Table, Form, Accordion } from "react-bootstrap";
import IContact, { IContactCreateOrUpdate } from "models/sql/IContact";
import { GlobalContext } from "./GlobalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faComments, faEnvelope, faPhone, faTrash, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

import './styles/ContactViewer.scss';
import ContactService from "services/ContactService";
import { convertToDateTimeLocalString, formatDateTime, isoDateTimeStringWithoutTimeZone } from "utilities/StringUtility";
import { addNewLogEntry } from "utilities/MiscUtilities";

export interface IContactViewerProps {
    service: any;
    entityId: number;
    canEdit?: boolean;
    className?: string;
    defaultOpen?: boolean;
    source?: "Dealer" | "FireDepartment";
}

const ContactViewer = (props: IContactViewerProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [contacts, setContacts] = useState<IContact[]>();
    const [contactModalShowing, setContactModalShowing] = useState(false);
    const [newFirstName, setNewFirstName] = useState("");
    const [newLastName, setNewLastName] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newRole, setNewRole] = useState("");
    const [newType, setNewType] = useState("");
    const [newSource, setNewSource] = useState("");
    const [newLastContactDate, setNewLastContactDate] = useState("");
    const [forceRefresh, setForceRefresh] = useState(new Date());
    const dealerRoles = ["Sales Rep", "Sales Manager", "Sales Director/VP", "Purchasing", "Owner/President", "Other"];
    const fireDepartmentRoles = ["BN Chief", "Chief", "Lieutenant", "Health & Safety", "Purchasing", "Other"];
    const roles =
        props.source === "Dealer" ?
            dealerRoles :
            props.source === "FireDepartment" ?
                fireDepartmentRoles :
                Array.from(new Set([...dealerRoles, ...fireDepartmentRoles]));
    const types = ["End Customer", "Dealer Rep", "Supplier/ Contractor"];
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            if (globalContext.roles.some(r => r === "Web.Contacts")) {
                setLoading(true);
                const accessToken = await globalContextRef.current.getAccessTokenAsync();
                const response = await props.service.GetContactsAsync(accessToken, props.entityId);
                setContacts(response.record);
                setLoading(false);
            }
        })();
    }, [props.entityId, forceRefresh]);

    const showAddContactModal = () => {
        setNewFirstName("");
        setNewLastName("");
        setNewPhone("");
        setNewEmail("");
        setNewRole("");
        setNewType("");
        setNewLastContactDate("");
        setContactModalShowing(true);
    };

    const hideAddContactModal = () => {
        setContactModalShowing(false);
    };

    const addContactAsync = async () => {
        var newContact: IContactCreateOrUpdate = {
            contactId: -1,
            email: newEmail,
            phone: newPhone,
            firstName: newFirstName,
            lastName: newLastName,
            role: newRole,
            lastContactDate: newLastContactDate || undefined,
            type: newType,
            source: newSource
        };

        const accessToken = await globalContextRef.current.getAccessTokenAsync();
        setLoading(true);
        const response = await props.service.CreateOrUpdateContactAsync(accessToken, props.entityId, newContact);
        setLoading(false);
        hideAddContactModal();
        setForceRefresh(new Date());
    };

    const deleteEntryAsync = async (contactId: number) => {
        if (window.confirm("Are you SURE you want to delete this contact?")) {
            const accessToken = await globalContextRef.current.getAccessTokenAsync();
            setLoading(true);
            const response = await props.service.DeleteContactAsync(accessToken, props.entityId, contactId);
            setLoading(false);
            setForceRefresh(new Date());
        }
    };

    const markContactedAsync = async (contact: IContact) => {
        if (window.confirm(`Mark ${contact.firstName} ${contact.lastName} as contacted just now?\r\nANY UNSAVED CHANGES TO THIS PAGE WILL BE LOST.`)) {
            if (contact) {
                globalContext.setLoading(true);
                const accessToken = await globalContext.getAccessTokenAsync();

                let data: IContactCreateOrUpdate = {
                    contactId: contact.contactId,
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    email: contact.email,
                    phone: contact.phone,
                    role: contact.role,
                    created: contact.created,
                    notes: contact.notes,
                    systemDataJson: contact.systemDataJson,
                    lastContactDate: (new Date()).toISOString(),
                    lastUpdated: contact.lastUpdated,
                    lastUpdatedBy: contact.lastUpdatedBy,
                    type: contact.type,
                    source: contact.source
                };

                data =
                    addNewLogEntry(
                        (new Date()),
                        `${globalContext.userName} marked as contacted`,
                        globalContext.userEmail,
                        101,
                        data)

                await ContactService.CreateOrUpdateAsync(accessToken, data);
                globalContext.setLoading(false);
                setForceRefresh(new Date());
            }
        }
    };

    if (globalContext.roles.some(r => r === "Web.Contacts")) {
        return (
            <>
                <Row className={`contact-viewer ${props.className} mb-3`}>
                    <Col>
                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }} defaultActiveKey={props.defaultOpen ? "0" : "1"}>
                            <Accordion.Item
                                eventKey="0">
                                <Accordion.Header><strong>Contacts</strong></Accordion.Header>
                                <Accordion.Body>
                                    {
                                        loading &&
                                        <h4 className="text-center">Loading...</h4>
                                    }
                                    {
                                        !loading &&
                                        <>
                                            <Table striped bordered hover size="sm">
                                                <thead>
                                                    <tr>
                                                        <th className={`align-top`}>Full Name</th>
                                                        <th className={`align-top`}>Actions</th>
                                                        <th className={`align-top`}>Phone</th>
                                                        <th className={`align-top`}>Email</th>
                                                        <th className={`align-top`}>Role</th>
                                                        <th className={`align-top`}>Type</th>
                                                        <th className={`align-top`}>Source</th>
                                                        <th className={`align-top`}>Last Contacted</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        (!contacts || contacts.length < 1) &&
                                                        <tr>
                                                            <td colSpan={7}>
                                                                <h4 style={{ width: "100%" }} className="text-center pt-2">No Contacts</h4>
                                                            </td>
                                                        </tr>
                                                    }
                                                    {
                                                        contacts &&
                                                        contacts.length > 0 &&
                                                        contacts.map(
                                                            (row, index) =>
                                                                <tr
                                                                    style={{ cursor: "pointer" }}
                                                                    key={index}
                                                                    onClick={() => { window.open(`/Contacts/Detail/${row.contactId}`) }}>
                                                                    <td className={`align-top`}>{row.firstName} {row.lastName}</td>
                                                                    <td className={`align-top`}>
                                                                        {
                                                                            row.email &&
                                                                            <a
                                                                                className="me-2"
                                                                                href={`Mailto:${row.email}`}
                                                                                onClick={(e) => { e.stopPropagation(); }}>
                                                                                <FontAwesomeIcon icon={faEnvelope as IconProp} /></a>
                                                                        }
                                                                        {
                                                                            row.phone &&
                                                                            <a
                                                                                className="me-2"
                                                                                href={`Tel:${row.email}`}
                                                                                onClick={(e) => { e.stopPropagation(); }}>
                                                                                <FontAwesomeIcon icon={faPhone as IconProp} /></a>
                                                                        }
                                                                        {
                                                                            props.canEdit &&
                                                                            <FontAwesomeIcon
                                                                                className="me-2"
                                                                                style={{ cursor: "pointer" }}
                                                                                icon={faTrash as IconProp}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    deleteEntryAsync(row.contactId);
                                                                                }} />
                                                                        }
                                                                        <span
                                                                            className="quick-action pe-1"
                                                                            title="Mark as contacted"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                e.preventDefault();
                                                                                markContactedAsync(row);
                                                                                return false;
                                                                            }}><FontAwesomeIcon icon={faComments as IconProp} /></span>
                                                                    </td>
                                                                    <td className={`align-top`}>
                                                                        <a
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                            }}
                                                                            href={`Tel:${row.phone}`}>
                                                                            <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {row.phone}</a>
                                                                    </td>
                                                                    <td className={`align-top`}>
                                                                        <a
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                            }}
                                                                            href={`MailTo:${row.email}`}>
                                                                            <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {row.email}</a>
                                                                    </td>
                                                                    <td className={`align-top`}>{row.role}</td>
                                                                    <td className={`align-top`}>{row.type}</td>
                                                                    <td className={`align-top`}>{row.source}</td>
                                                                    <td className={`align-top`}>{formatDateTime(row.lastContactDate)}</td>
                                                                </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </Table>
                                            <Button
                                                className="w-100 mb-2"
                                                onClick={showAddContactModal}>Add New Contact</Button>
                                        </>
                                    }
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </Col>
                </Row>

                <Modal
                    show={contactModalShowing}
                    id="lookupModal"
                    onHide={hideAddContactModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Contact</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>First Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setNewFirstName(e.target.value);
                                                }
                                            }
                                            value={newFirstName}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Last Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setNewLastName(e.target.value);
                                                }
                                            }
                                            value={newLastName}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control
                                            type="text"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setNewPhone(e.target.value);
                                                }
                                            }
                                            value={newPhone}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="text"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setNewEmail(e.target.value);
                                                }
                                            }
                                            value={newEmail}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Role</Form.Label>
                                        <Form.Select
                                            onChange={async (e) => {
                                                setNewRole(e.target.value);
                                            }}
                                            value={newRole}>
                                            <option key={"empty"} value={""}>-</option>
                                            {
                                                roles.map((s, index) => <option key={s} value={s}>{s}</option>)
                                            }
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Type</Form.Label>
                                        <Form.Select
                                            onChange={async (e) => {
                                                setNewType(e.target.value);
                                            }}
                                            value={newType}>
                                            <option key={"empty"} value={""}>-</option>
                                            {
                                                types.map((s, index) => <option key={s} value={s}>{s}</option>)
                                            }
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Source</Form.Label>
                                        {
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setNewSource(e.target.value);
                                                    }
                                                }
                                                value={newSource}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Last Contacted</Form.Label>
                                        {
                                            <Form.Control
                                                type="datetime-local"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setNewLastContactDate(new Date(e.target.value).toISOString());
                                                    }
                                                }
                                                value={newLastContactDate ? convertToDateTimeLocalString(newLastContactDate) : undefined}
                                            />
                                        }
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={hideAddContactModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={addContactAsync}>
                            Add
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
    else {
        return <></>
    }
}

export default ContactViewer;